import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Theme snapshot - stores the full content for undo/redo
export type ThemeSnapshot = {
  themeId: string | null;
  content: string; // JSON string of theme content
};

interface ThemeHistoryStore {
  // State
  history: ThemeSnapshot[];
  currentIndex: number;

  // Actions
  pushHistory: (snapshot: ThemeSnapshot) => void;
  undo: () => ThemeSnapshot | null;
  redo: () => ThemeSnapshot | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clearHistory: () => void;
  initHistory: (snapshot: ThemeSnapshot) => void;
}

export const useThemeHistoryStore = create<ThemeHistoryStore>()(
  devtools(
    (set, get) => ({
      history: [],
      currentIndex: -1,

      // Initialize history with first snapshot
      initHistory: (snapshot) =>
        set({
          history: [{ ...snapshot }],
          currentIndex: 0,
        }, false, 'initHistory'),

      // Push new snapshot to history
      pushHistory: (snapshot) =>
        set((state) => {
          // Truncate future history if we're not at the end
          const newHistory = state.history.slice(0, state.currentIndex + 1);

          // Don't push if content is same as last entry
          const lastEntry = newHistory[newHistory.length - 1];
          if (lastEntry && lastEntry.content === snapshot.content) {
            return state;
          }

          newHistory.push({ ...snapshot });

          // Limit history size to 50
          if (newHistory.length > 50) {
            newHistory.shift();
            return {
              history: newHistory,
              currentIndex: newHistory.length - 1,
            };
          }

          return {
            history: newHistory,
            currentIndex: newHistory.length - 1,
          };
        }, false, 'pushHistory'),

      // Undo - return previous snapshot
      undo: () => {
        const state = get();
        if (state.currentIndex <= 0) return null;

        const newIndex = state.currentIndex - 1;
        set({ currentIndex: newIndex }, false, 'undo');

        return { ...state.history[newIndex] };
      },

      // Redo - return next snapshot
      redo: () => {
        const state = get();
        if (state.currentIndex >= state.history.length - 1) return null;

        const newIndex = state.currentIndex + 1;
        set({ currentIndex: newIndex }, false, 'redo');

        return { ...state.history[newIndex] };
      },

      canUndo: () => {
        const state = get();
        return state.currentIndex > 0;
      },

      canRedo: () => {
        const state = get();
        return state.currentIndex < state.history.length - 1;
      },

      clearHistory: () =>
        set({
          history: [],
          currentIndex: -1,
        }, false, 'clearHistory'),
    }),
    {
      name: 'ThemeHistory',
      enabled: true,
    }
  )
);
